<template>
  <Card>
    <CardHeader class="flex flex-row items-start justify-between gap-4">
      <div class="min-w-0 space-y-1">
        <p class="text-xs uppercase tracking-wide text-muted-foreground">{{ showTitle ? 'Task Details' : 'Description' }}</p>
        <CardTitle v-if="showTitle" class="break-words text-xl">
          {{ taskTitle }}
        </CardTitle>
      </div>

      <Button v-if="!readonly" type="button" size="sm" variant="outline" class="shrink-0" @click="openEditModal">
        <Pencil class="h-3.5 w-3.5" />
        Edit
      </Button>
    </CardHeader>

    <CardContent class="pt-0">
      <template v-if="hasDescription">
        <div class="relative">
          <div :class="['tiptap-viewer bg-transparent', isExpanded ? 'max-h-none' : 'max-h-80 overflow-hidden']">
            <EditorContent :editor="viewerEditor" />
          </div>

          <div v-if="!isExpanded" class="pointer-events-none absolute inset-x-0 bottom-0 h-16 rounded-b-md bg-linear-to-t from-background via-background/85 to-transparent" />
        </div>

        <div class="mt-3 flex justify-end">
          <Button variant="link" size="sm" type="button" class="h-auto px-0" @click="toggleExpanded">
            {{ isExpanded ? 'Collapse' : 'Expand fully' }}
          </Button>
        </div>
      </template>
      <div v-else class="rounded-md border border-dashed border-input/70 px-4 py-8 text-sm text-muted-foreground">No description provided.</div>
    </CardContent>
  </Card>

  <TaskTitleDescriptionEditModal ref="taskTitleDescriptionEditModal" :task="task" :context="context" />
</template>

<script setup>
import Image from '@tiptap/extension-image'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { Pencil } from 'lucide-vue-next'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../../resources/js/components/ui/card/index.js'
import TaskTitleDescriptionEditModal from './TaskTitleDescriptionEditModal.vue'
import { createEmptyTaskRichTextDocument, parseTaskRichTextDescription, taskRichTextDocumentHasRenderableContent } from '../../utils/taskRichText.js'

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  context: {
    type: String,
    default: 'task-show',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
})

const taskTitleDescriptionEditModal = ref(null)
const isExpanded = ref(false)
const taskTitle = computed(() => {
  const title = typeof props.task?.title === 'string' ? props.task.title.trim() : ''

  return title !== '' ? title : 'Untitled task'
})
const descriptionDocument = computed(() => parseTaskRichTextDescription(props.task?.description ?? ''))
const hasDescription = computed(() => taskRichTextDocumentHasRenderableContent(descriptionDocument.value))

const viewerEditor = useEditor({
  extensions: [StarterKit, Image],
  content: createEmptyTaskRichTextDocument(),
  editable: false,
  editorProps: {
    attributes: {
      class: 'text-sm text-foreground focus:outline-none',
    },
  },
})

const syncViewerEditor = (documentNode) => {
  if (!viewerEditor.value) {
    return
  }

  viewerEditor.value.commands.setContent(documentNode, false)
}

const openEditModal = () => {
  if (props.readonly) {
    return
  }

  taskTitleDescriptionEditModal.value?.openModal?.()
}

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

onBeforeUnmount(() => {
  viewerEditor.value?.destroy()
})

watch(
  viewerEditor,
  (currentEditor) => {
    if (!currentEditor) {
      return
    }

    syncViewerEditor(descriptionDocument.value)
  },
  {
    immediate: true,
  },
)

watch(descriptionDocument, (currentDocument) => {
  isExpanded.value = false
  syncViewerEditor(currentDocument)
})
</script>

<style scoped>
.tiptap-viewer :deep(.ProseMirror) {
  white-space: pre-wrap;
}

.tiptap-viewer :deep(.ProseMirror ul),
.tiptap-viewer :deep(.ProseMirror ol) {
  padding-left: 1.5rem;
}

.tiptap-viewer :deep(.ProseMirror ul) {
  list-style-type: disc;
}

.tiptap-viewer :deep(.ProseMirror ol) {
  list-style-type: decimal;
}

.tiptap-viewer :deep(.ProseMirror h2) {
  font-size: 1.25rem;
  font-weight: 600;
}

.tiptap-viewer :deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
}
</style>
